import React, { FC } from 'react'
import { Flex, Skeleton, Tag, Typography } from 'antd'
import { LineChartOutlined, LockOutlined } from '@ant-design/icons'
import { EmptyState, Well } from '@pushly/aqe/lib/components'
import { CURATOR_DESCRIPTION, domainHasFlag, HEALTH_DESCRIPTION, STEP_DEFAULTS_DESCRIPTION } from './constants'
import { FEAT_INTEGRATIONS_VESPA_REC } from '../../constants'
import { DomainDto } from '../../dtos/domain'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { getClassNames } from '../../_utils/classnames'

interface ICuratorSettings {
    loading: boolean
    domain?: DomainDto
}

interface IStepDefault {
    label: string
    summary: string
}

/**
 * The values a new Curator step starts from. They are set on the step itself,
 * so this well reports them rather than editing them.
 */
const STEP_DEFAULTS: IStepDefault[] = [
    {
        label: 'Content window',
        summary: 'Articles published in the last 12 hours.',
    },
    {
        label: 'Repeat suppression',
        summary: 'An article already sent to a segment is not offered to it again for 30 days.',
    },
    {
        label: 'Interest Map minimum',
        summary: 'A segment needs 500 clicks before it gets a taste vector. Below that it falls back to recency.',
    },
]

export const CuratorSettings: FC<ICuratorSettings> = ({ loading, domain }) => {
    const enabled = domainHasFlag(domain, FEAT_INTEGRATIONS_VESPA_REC)

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('curator-settings')}>
            <Well
                title="Pushly AI Curator"
                actions={<Tag color={enabled ? 'success' : undefined}>{enabled ? 'Live' : 'Off'}</Tag>}
                footerMode="hidden"
            >
                <Skeleton loading={loading} active={true} title={false}>
                    <Typography.Paragraph className="ai-muted-text">{CURATOR_DESCRIPTION}</Typography.Paragraph>
                </Skeleton>
            </Well>

            {enabled ? (
                <>
                    <Well title="Curation health" footerMode="hidden">
                        <Flex vertical gap={12}>
                            <Typography.Text className="ai-muted-text">{HEALTH_DESCRIPTION}</Typography.Text>

                            <Skeleton loading={loading} active={true} title={false}>
                                <EmptyState
                                    size="small"
                                    icon={<LineChartOutlined />}
                                    iconVariant="chip"
                                    title="No coverage data yet"
                                    description="Coverage is computed from the content library rather than configured, so there is nothing to set up here. Nothing has been reported for this domain."
                                />
                            </Skeleton>
                        </Flex>
                    </Well>

                    <Well title="Step defaults" footerMode="hidden">
                        <Flex vertical gap={12}>
                            <Typography.Text className="ai-muted-text">{STEP_DEFAULTS_DESCRIPTION}</Typography.Text>

                            <Skeleton loading={loading} active={true} title={false}>
                                <Flex vertical>
                                    {STEP_DEFAULTS.map((row) => (
                                        <Well.Section key={row.label} title={row.label}>
                                            <Typography.Text>{row.summary}</Typography.Text>
                                        </Well.Section>
                                    ))}
                                </Flex>
                            </Skeleton>
                        </Flex>
                    </Well>
                </>
            ) : (
                <Well hideHeader={true} footerMode="hidden">
                    <EmptyState
                        icon={<LockOutlined />}
                        iconVariant="chip"
                        title="Curator is not enabled for this domain"
                        description="Talk to your account admin about sending each audience the story it is most likely to open."
                    />
                </Well>
            )}
        </Flex>
    )
}
